import React, { useEffect } from 'react';
import * as yup from 'yup';
import { Identifier } from '@utils/utils';
import { Dialog } from 'primereact/dialog';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Dayjs } from 'dayjs';
import useForm from '@hooks/useForm/useForm';
import HorizontalFieldValue from '../../customer/carriers/HorizontalFieldValue';
import { Button } from 'primereact/button';
import {
  dayjsNowInTz,
} from '@utils/dateWithTz.tsx';
import ValidationDiv from '@utils/validation';
import ButtonLayout from '@components/obsolete/Form/ButtonLayout/ButtonLayout.tsx';
import InputDateAndTime from '@components/InputDateAndTime/InputDateAndTime.tsx';

interface ComponentProps {
  locationId: Identifier;
  zipCode: string;
  countryCode: string;
  timezoneId: string;
  propertyName: string;
  onConfirm: (locationId: Identifier, when: Dayjs, timezoneId: string) => void;
  onCancel: () => void;
  visible: boolean;
  dataTestId?: string;
}

interface Form {
  dateTime: Dayjs;
  timezone: string;
}

const LoadingUnloadingConfirmDialog: React.FC<ComponentProps> = ({ propertyName, locationId, onConfirm, countryCode, zipCode, timezoneId, onCancel, visible, dataTestId }) => {
  const { tr } = useTranslationLgs();

  const initialValues: Form = {
    dateTime: dayjsNowInTz(timezoneId),
    timezone: timezoneId,
  };

  const validationScheme = yup.object().shape({
    dateTime: yup.mixed().required(),
    timezone: yup.string().required(),
  });

  const form = useForm<Form>(
    validationScheme,
    initialValues,
    () => {
      onConfirm(locationId, form.values.dateTime.utc(), form.values.timezone);
    },
    false,
    false,
    () => {},
  );

  useEffect(() => {
    if (visible) {
      form.setValues({
        dateTime: dayjsNowInTz(timezoneId),
        timezone: timezoneId,
      });
    }
  }, [visible, timezoneId]);

  const resolveHeading = (propertName: string) => {
    switch (propertName) {
      case 'unloadingLocationFinished':
        return tr('LoadingUnloadingConfirmDialog.unloadingLocationFinished', 'Confirm unloading');
      case 'loadingLocationFinished':
        return tr('LoadingUnloadingConfirmDialog.loadingLocationFinished', 'Confirm loading');
      case 'noActionFinishedAt':
        return tr(`LoadingUnloadingConfirmDialog.noActionFinishedAt`, `Confirm location`);
      default:
        return 'NA';
    }
  };

  const resolveQuestion = (propertName: string) => {
    switch (propertName) {
      case 'unloadingLocationFinished':
        return tr(`LoadingUnloadingConfirmDialog.wouldYouLikeToConfirmUnloading`, `Would you like to confirm unloading at location: {{countryCode}}, {{zipCode}}?`, {
          countryCode,
          zipCode,
        });
      case 'loadingLocationFinished':
        return tr(`LoadingUnloadingConfirmDialog.wouldYouLikeToConfirmLoading`, `Would you like to confirm loading at location: {{countryCode}}, {{zipCode}}?`, {
          countryCode,
          zipCode,
        });
      case 'noActionFinishedAt':
        return tr('LoadingUnloadingConfirmDialog.wouldYouLikeToConfirmArrival', 'Would you like to confirm arrival at location: {{countryCode}}, {{zipCode}}?', {
          countryCode,
          zipCode,
        });
      default:
        return 'NA';
    }
  };

  return (
    <>
      {/*// @ts-ignore*/}
      <Dialog visible={visible} baseZIndex={1000000} onHide={() => onCancel()} header={<h4>{resolveHeading(propertyName)}</h4>} className="dialog-confirm dialog-md">
        <strong>{resolveQuestion(propertyName)}</strong>
        <div className="p-mt-3">
          <HorizontalFieldValue
            label={tr(`LoadingUnloadingConfirmDialog.dateAndTime`, `Date & Time`)}
            value={
              <>
                <InputDateAndTime
                  value={form.values.dateTime}
                  timeZone={form.values.timezone}
                  onChange={(dateTime, timezone) => {
                    form.setFieldValue('dateTime', dateTime);
                    if (timezone) {
                      form.setFieldValue('timezone', timezone);
                    }
                  }}
                  showTime={true}
                  dataTestIdCalendar={`${dataTestId}-calendar`}
                  dataTestIdTimezone={`${dataTestId}-calendar-timezone`}
                />
                <ValidationDiv errors={form.validationErrors} path={'dateTime'} />
              </>
            }
          />
        </div>
        <ButtonLayout wrapper="WITHOUT_PADDING_BOTTOM">
          <Button
            label={tr(`LoadingUnloadingConfirmDialog.save`, `Save`)}
            className="p-mr-2"
            onClick={e => {
              form.validateAndSend();
            }}
            data-testid={`${dataTestId}-save`}
          />
          <Button label={tr(`LoadingUnloadingConfirmDialog.cancel`, `Cancel`)} className="p-button-text" onClick={() => onCancel()} />
        </ButtonLayout>
      </Dialog>
    </>
  );
};

export default LoadingUnloadingConfirmDialog;
